// THE RUNNER'S BOARD (mig 276)
// One office runner, one bike. The problem was never his route — it was that a second
// request for the same part of town arrived after he had gone. So the whole value of this
// screen is that it can be read BEFORE he leaves, and that it is grouped by area.
//
// No run, no dispatcher, no route, no kilometres. Deliberately.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const C = { navy: '#3B3A8F', red: '#A32D2D', amber: '#B45309', green: '#166534', muted: '#888' };
const inp = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 };
const btn = { padding: '7px 14px', borderRadius: 6, fontSize: 12.5, cursor: 'pointer', fontWeight: 600, border: '1px solid #ddd' };

export default function Runner() {
  const { user } = useAuth();
  const isAreaAdmin = ['admin', 'management'].includes(user?.role);
  const [tab, setTab] = useState('board');
  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#1a1a2e' }}>🛵 Runner</h2>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: C.muted }}>
        Errands collect here, grouped by area, so one trip can cover several. Anyone may add one.
      </p>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 16 }}>
        {[['board', '📋 Board'], ['history', '🗂 History'], ...(isAreaAdmin ? [['areas', '📍 Areas']] : [])].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13,
            fontWeight: tab === k ? 700 : 400, color: tab === k ? C.navy : C.muted,
            borderBottom: tab === k ? `2px solid ${C.navy}` : '2px solid transparent' }}>{l}</button>
        ))}
      </div>
      {tab === 'board' && <Board user={user} />}
      {tab === 'history' && <History />}
      {tab === 'areas' && isAreaAdmin && <Areas />}
    </div>
  );
}

function Board({ user }) {
  const [data, setData] = useState(null);
  const [areas, setAreas] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ task: '', destination_id: '', priority: 'normal', preferred_date: '', notes: '' });

  const load = useCallback(() => {
    api.get('/runner/errands?status=open').then(r => setData(r.data.data)).catch(e => setErr(e.response?.data?.error || 'Could not load the board'));
    api.get('/runner/destinations?active=1').then(r => setAreas(r.data.data || [])).catch(() => setAreas([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.task.trim()) { setErr('Say what needs fetching.'); return; }
    setBusy('add'); setErr('');
    try {
      await api.post('/runner/errands', { ...form, destination_id: form.destination_id || null, preferred_date: form.preferred_date || null });
      setForm({ task: '', destination_id: '', priority: 'normal', preferred_date: '', notes: '' }); load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not add it'); } finally { setBusy(''); }
  };
  const done = async (e2) => {
    setBusy(e2.id); setErr('');
    try { await api.post(`/runner/errands/${e2.id}/done`, { note: null }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(''); }
  };
  const cancel = async (e2) => {
    const reason = window.prompt(`Cancel "${e2.task}" — why?`);
    if (reason === null) return;
    if (!reason.trim()) { setErr('A cancellation needs a reason.'); return; }
    setBusy(e2.id); setErr('');
    try { await api.post(`/runner/errands/${e2.id}/cancel`, { reason: reason.trim() }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(''); }
  };
  // Setting the area on an existing errand is how the unassigned group empties as the list fills.
  const setArea = async (e2, destination_id) => {
    setBusy(e2.id);
    try { await api.patch(`/runner/errands/${e2.id}`, { destination_id }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(''); }
  };

  if (!data) return <div style={{ color: C.muted, fontSize: 13 }}>{err || 'Loading…'}</div>;

  return (
    <div>
      <div style={{ border: '1px solid #E8E7E7', borderRadius: 10, padding: '14px 16px', marginBottom: 18, background: '#FBFBFD' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
          <div><label style={lbl}>What is needed *</label>
            <input style={inp} value={form.task} placeholder="e.g. 2 kg twill tape, navy"
              onChange={e => setForm(f => ({ ...f, task: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') add(); }} /></div>
          <div><label style={lbl}>Area</label>
            <select style={inp} value={form.destination_id} onChange={e => setForm(f => ({ ...f, destination_id: e.target.value }))}>
              {/* The list ships empty on purpose. Saying so beats an empty dropdown that
                  reads as a broken form. */}
              <option value="">{areas.length ? '— not sure yet —' : 'no areas set up yet'}</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></div>
          <div><label style={lbl}>Priority</label>
            <select style={inp} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              <option value="normal">Normal</option><option value="urgent">Urgent</option>
            </select></div>
          <div><label style={lbl}>Needed by</label>
            <input type="date" style={inp} value={form.preferred_date} onChange={e => setForm(f => ({ ...f, preferred_date: e.target.value }))} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button disabled={busy === 'add'} onClick={add} style={{ ...btn, background: C.navy, color: '#fff', border: 'none' }}>
            {busy === 'add' ? 'Adding…' : '+ Add errand'}</button>
        </div>
      </div>

      {err && <div style={{ background: '#FDECEC', border: '1px solid #f3c2c2', color: C.red, borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

      {data.total === 0
        ? <div style={{ fontSize: 13, color: C.muted }}>Nothing waiting. Anything added here will be grouped by area.</div>
        : <>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
            <strong>{data.total}</strong> waiting{data.unassigned > 0 && <> · <strong>{data.unassigned}</strong> with no area set</>}
          </div>
          {data.groups.map(g => (
            <div key={g.key} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: g.area_name ? C.navy : C.amber, marginBottom: 6 }}>
                {g.area_name || 'Area not set'}
                <span style={{ fontWeight: 400, color: C.muted }}> · {g.errands.length}</span>
                {/* The unassigned group is the backlog telling whoever curates the list
                    which areas are still missing. Named as such, not hidden. */}
                {!g.area_name && <span style={{ fontWeight: 400, color: C.muted, fontSize: 11 }}> — set an area so these group with the rest</span>}
              </div>
              {g.errands.map(e2 => (
                <div key={e2.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                  border: '1px solid #eee', borderLeft: `3px solid ${e2.priority === 'urgent' ? C.red : '#ddd'}`,
                  borderRadius: 6, padding: '7px 10px', marginBottom: 5 }}>
                  <span style={{ flex: 1, fontSize: 13, minWidth: 200 }}>
                    {e2.task}
                    <span style={{ color: C.muted, fontSize: 11 }}>
                      {' '}· {e2.requested_by_name}
                      {e2.waiting_days > 0 && <span style={{ color: e2.waiting_days >= 3 ? C.red : C.muted, fontWeight: e2.waiting_days >= 3 ? 700 : 400 }}> · waiting {e2.waiting_days}d</span>}
                      {e2.preferred_date && ` · needed by ${String(e2.preferred_date).slice(0, 10)}`}
                    </span>
                  </span>
                  {e2.priority === 'urgent' && <span style={{ fontSize: 10.5, background: '#FDECEC', color: C.red, borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>urgent</span>}
                  {!e2.destination_id && areas.length > 0 && (
                    <select style={{ ...inp, width: 150 }} value="" onChange={ev => ev.target.value && setArea(e2, ev.target.value)}>
                      <option value="">set area…</option>
                      {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>)}
                  <button disabled={busy === e2.id} onClick={() => done(e2)} style={{ ...btn, background: '#EAF7EE', color: C.green }}>✓ Done</button>
                  {(['admin', 'management'].includes(user?.role) || e2.requested_by === user?.id) &&
                    <button disabled={busy === e2.id} onClick={() => cancel(e2)} style={{ ...btn, background: '#F3F4F6', color: C.muted }}>Cancel</button>}
                </div>
              ))}
            </div>
          ))}
        </>}
    </div>
  );
}

function History() {
  const [status, setStatus] = useState('done');
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); api.get(`/runner/errands?status=${status}`).then(r => setRows(r.data.data)).catch(() => setRows({ groups: [], total: 0 })); }, [status]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['done', 'Done'], ['cancelled', 'Cancelled']].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} style={{ ...btn, background: status === k ? '#EEEDF8' : '#fff', color: status === k ? C.navy : C.muted }}>{l}</button>
        ))}
      </div>
      {!rows ? <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
        : rows.total === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>Nothing here yet.</div>
        : rows.groups.flatMap(g => g.errands).map(e => (
          <div key={e.id} style={{ borderBottom: '1px solid #f4f4f8', padding: '7px 0', fontSize: 12.5 }}>
            <strong>{e.task}</strong>
            <span style={{ color: C.muted }}> · {e.area_name || 'no area'} · asked by {e.requested_by_name}</span>
            {status === 'done'
              ? <span style={{ color: C.green }}> · done by {e.done_by_name}</span>
              : <span style={{ color: C.red }}> · cancelled by {e.cancelled_by_name} — {e.cancel_reason}</span>}
          </div>
        ))}
    </div>
  );
}

function Areas() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get('/runner/destinations').then(r => setRows(r.data.data || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setErr('');
    try { await api.post('/runner/destinations', { name: name.trim() }); setName(''); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not add'); }
  };
  const toggle = async (a) => {
    try { await api.patch(`/runner/destinations/${a.id}`, { is_active: !a.is_active }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not change'); }
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.6, maxWidth: 720 }}>
        The areas the runner actually rides to — not the shops he buys from, and not the vendor
        addresses. Nothing seeds this list: of his fifty recorded trips none went to a supplier in
        the register. It is filled by sitting with him and naming the places.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, maxWidth: 460 }}>
        <input style={inp} value={name} placeholder="e.g. Focal Point" onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }} />
        <button onClick={add} style={{ ...btn, background: C.navy, color: '#fff', border: 'none', whiteSpace: 'nowrap' }}>+ Add area</button>
      </div>
      {err && <div style={{ background: '#FDECEC', border: '1px solid #f3c2c2', color: C.red, borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {!rows ? <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
        : rows.length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>No areas yet. Add the first one above.</div>
        : rows.map(a => (
          <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid #f4f4f8', padding: '7px 0', opacity: a.is_active ? 1 : 0.55 }}>
            <span style={{ flex: 1, fontSize: 13 }}>{a.name}
              <span style={{ color: C.muted, fontSize: 11 }}> · {a.open_errands} waiting</span></span>
            {/* Retired, never deleted — errands are filed under an area and the FK refuses
                to orphan them. */}
            <button onClick={() => toggle(a)} style={{ ...btn, background: a.is_active ? '#EAF7EE' : '#F3F4F6', color: a.is_active ? C.green : C.muted }}>
              {a.is_active ? 'In use' : 'Retired'}</button>
          </div>
        ))}
    </div>
  );
}
